/**
 * `CompleteStep` — ADR-0049 step 3 (Complete). The verify-gated
 * celebration screen, reached when the merchant has completed proof
 * at step 2. Renders ONE of two faces based on the freshly-fetched
 * server status (NOT the cached `useSites()` object — see "Freshness"
 * below) of the active site:
 *
 *   - SUCCESS face — when `status ∈ {discovery_active,
 *     transactional_active}`:
 *       A compact hero (founder 2026-10-29: no step kicker, no big check
 *       tile, no "still waiting" line; round 2: centered and stacked, no
 *       line under the headline, no back link): `You're <em>live</em>
 *       on {domain}.` above the `Open dashboard →` CTA that calls
 *       `enableSampleTraffic(true)` and routes to `mandates`. Below it:
 *       the coverage card (its rows are the checklist), then
 *       `ProtocolStrip` (registry-driven chips that link to the docs).
 *
 *   - SAFETY-NET face — when `status` is anything else (e.g. a merchant
 *     who navigated forward via a stale URL, a forced `goTo(3)` on a
 *     `pending_proof` site, or a race):
 *       An amber-bordered card with a `NOT YET VERIFIED` mono kicker,
 *       serif h3 `One step left — verify <domain>.`, sans body, and a
 *       `Back to verify →` button that calls `machine.back()`. This
 *       face does NOT claim the merchant is live, does NOT render the
 *       protocol advert, and does NOT show `Open dashboard`. This is
 *       the safety net the founder asked for.
 *
 * Verify-gate invariants per ADR-0049 §"Verify-gated Complete":
 *   - The "You're live" hero MUST NOT render unless the FRESHLY-FETCHED
 *     server status is `discovery_active` / `transactional_active` —
 *     single source of truth is the `verified` boolean computed at the
 *     top of the component, which reads `freshStatus` (the result of a
 *     `sites.get(active_site_id)` call on mount + on the post-OAuth
 *     return), falling back to `activeSite.status` only while the fetch
 *     is in flight.
 *   - `ProtocolStrip` MUST NOT render unless `verified === true`.
 *   - `enableSampleTraffic(true)` stays behind the `verified` gate
 *     (it's the Open-dashboard click handler, which only exists in the
 *     success face).
 *
 * Freshness (defect B, 2026-06-04): the `useSites()` context's
 * `activeSite` can be a stale snapshot after a delete + re-add, a
 * status change in another tab, or an interrupted onboarding flow that
 * landed back on step 4 via a URL-driven resume. The TopBar dot and the
 * SiteCard read `site.status` at a fresher point and could disagree
 * with this view. To prevent the "you're live" hero from rendering for
 * a server-side `pending_proof` site, CompleteStep does its own one-shot
 * `sites.get(site_id)` on mount (and re-fetches whenever
 * `?connected=shopify` is present at mount — a post-OAuth return
 * signal that the status may have just flipped). The fresh row is also
 * pushed through `updateSite()` + `setActiveSite()` so the TopBar +
 * SiteCard converge on the same truth.
 */

import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, Check } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { SiteStatus } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useSites } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import { InstallCoverageCard } from '../sites/InstallCoverageCard.js';
import { ProtocolStrip } from './ProtocolStrip.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface CompleteStepProps {
  setView: (v: View) => void;
  /** Steps the machine one back to the Install + verify screen. Used
   *  by the "NOT YET VERIFIED" safety-net face's `Back to verify`
   *  button when status isn't `*_active`. */
  onBack: () => void;
}

export function CompleteStep({
  setView,
  onBack,
}: CompleteStepProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const { activeSite, updateSite, setActiveSite } = useSites();

  // Consume the OAuth-return URL flag ONCE on mount (2026-07-07).
  //
  // The Shopify OAuth callback redirects to
  // `/onboarding?connected=shopify&site=<id>` (ADR-0053 §1.1). The
  // sibling `useShopifyOauthReturn` (mounted inside `InstallVerifyStep`)
  // already strips those params when its poll reaches a terminal state
  // — but that hook never runs when the merchant lands DIRECTLY on
  // step 3 (CompleteStep) because their site is already
  // `discovery_active` / `transactional_active`. In that path the
  // params persist. A merchant who switches browser tabs and later
  // refreshes at `/onboarding?connected=shopify&site=<id>` (or
  // `…&view=<X>`) re-lands on this same "You're live" confirmation
  // hero — the OAuth-return trigger keeps re-firing.
  //
  // Fix: strip `connected` + `site` on mount so a refresh / tab-return
  // is treated as ordinary navigation, not a fresh OAuth return. The
  // "You're live" hero itself still renders as long as the fresh
  // server status is verified — the merchant is not deprived of the
  // confirmation on the FIRST landing (this effect runs after render).
  // Symmetric with the terminal cleanup in `useShopifyOauthReturn`.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      let mutated = false;
      if (url.searchParams.has('connected')) {
        url.searchParams.delete('connected');
        mutated = true;
      }
      if (url.searchParams.has('site')) {
        url.searchParams.delete('site');
        mutated = true;
      }
      if (!mutated) return;
      // `replaceState` (not `pushState`): the OAuth-return URL is a
      // one-shot trigger, not a history entry the merchant should be
      // able to Back-button into.
      window.history.replaceState(
        {},
        '',
        url.pathname + url.search + url.hash,
      );
    } catch {
      /* private-mode / sandbox iframe — degrade silently */
    }
  }, []);

  // Defect B (2026-06-04): the `useSites()` context's activeSite can be
  // a stale snapshot (delete + re-add, status change in another tab,
  // URL-driven resume). Read fresh server truth on mount + on a
  // post-OAuth return so the "you're live" hero never renders for a
  // server-side `pending_proof` site. While the fetch is in flight,
  // fall back to the context status — the safety-net face is the
  // honest default and won't false-positive the hero.
  const [freshStatus, setFreshStatus] = useState<SiteStatus | null>(null);
  const site_id = activeSite?.site_id ?? null;
  useEffect(() => {
    if (site_id === null) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const fresh = await api.sites.get(site_id);
        if (cancelled || fresh === null) return;
        setFreshStatus(fresh.status);
        // Push the fresh row into the shared context so the TopBar
        // dot + SiteCard converge with this view — no contradiction
        // across surfaces.
        updateSite(site_id, fresh);
        setActiveSite(fresh);
      } catch {
        /* swallow — safety-net face is the honest default */
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [site_id, api, setActiveSite, updateSite]);

  const effectiveStatus: SiteStatus | undefined =
    freshStatus ?? activeSite?.status;
  const verified =
    effectiveStatus === 'discovery_active' ||
    effectiveStatus === 'transactional_active';

  const domain = activeSite?.domain ?? 'your site';

  const openDashboard = (): void => {
    if (activeSite) {
      void api.sites.enableSampleTraffic(activeSite.site_id, true);
    }
    setView('mandates');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {verified ? (
        <>
          <div
            className="flex flex-col items-center text-center gap-5"
            data-testid="complete-hero"
          >
            <h3
              style={{
                fontFamily: serifFont,
                fontSize: 'clamp(28px, 3.2vw, 36px)',
                lineHeight: 1.15,
                color: C.text,
                letterSpacing: -0.8,
                margin: 0,
                overflowWrap: 'anywhere',
                maxWidth: 640,
              }}
            >
              <span
                aria-hidden
                className="inline-flex items-center justify-center"
                style={{ width: 24, height: 24, marginRight: 12, verticalAlign: '0.12em', background: C.limeDim, border: `1px solid ${C.limeFg}`, borderRadius: 2 }}
              >
                <Check size={14} color={C.limeFg} strokeWidth={2.5} />
              </span>
              {/* Non-breaking hyphens keep a domain on one line unless it
                  cannot fit at all (then overflowWrap breaks it). */}
              You're <em style={{ color: C.limeFg }}>live</em> on{' '}
              <span>{domain.replace(/-/g, '\u2011')}.</span>
            </h3>
            <button
              type="button"
              onClick={openDashboard}
              className="flex items-center justify-center gap-2 px-6 w-full sm:w-auto"
              style={{
                background: C.lime,
                border: 'none',
                color: C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 2,
                height: 44,
                cursor: 'pointer',
              }}
            >
              Open dashboard <ArrowRight size={15} />
            </button>
          </div>

          {/* ADR-0211 §5 — what is actually covered and at most ONE next
              step. Fail-soft: renders nothing when the read fails. CTAs
              land in Settings via the `?settings_tab=` one-shot channel. */}
          {activeSite !== null ? (
            <div className="mt-8">
              <InstallCoverageCard
                site={activeSite}
                variant="onboarding"
                onNavigate={(target) => {
                  if (typeof window !== 'undefined') {
                    try {
                      const url = new URL(window.location.href);
                      url.searchParams.set('settings_tab', target === 'endpoint' || target === 'install' ? 'install' : 'integrations');
                      window.history.replaceState({}, '', url.toString());
                    } catch {
                      // URL manipulation is best-effort.
                    }
                  }
                  setView('settings');
                }}
              />
            </div>
          ) : null}

          <div className="mt-6">
            <ProtocolStrip />
          </div>
        </>
      ) : (
        <Card className="p-6" style={{ borderColor: C.amberFg }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.amberFg,
              letterSpacing: 2,
              marginBottom: 10,
            }}
          >
            NOT YET VERIFIED
          </div>
          <h3
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(28px, 4vw, 36px)',
              lineHeight: 1.1,
              color: C.text,
              letterSpacing: -1,
              marginBottom: 12,
            }}
          >
            One step left — verify <em>{domain}</em>.
          </h3>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.muted,
              lineHeight: 1.55,
              marginBottom: 20,
              maxWidth: 560,
            }}
          >
            You haven't proven ownership of {domain} yet. Sill won't
            record anything until you do. Head back to verify.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-5 py-2.5"
            style={{
              background: C.lime,
              border: 'none',
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 2,
            }}
          >
            <ChevronLeft size={15} /> Back to verify
          </button>
        </Card>
      )}
    </motion.div>
  );
}
